import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { portfolioItems } from '../mock';
import Lightbox from './Lightbox';
import { useLightbox } from '../hooks/useLightbox';

const categories = [
  { key: 'all', label: 'All' },
  { key: 'wedding', label: 'Wedding' },
  { key: 'corporate', label: 'Corporate' },
  { key: 'private', label: 'Private' },
  { key: 'fashion', label: 'Fashion' },
];

const Portfolio = () => {
  const [filter, setFilter] = useState('all');
  const lightbox = useLightbox();

  const items = filter === 'all' ? portfolioItems : portfolioItems.filter((p) => p.category === filter);

  // Photos to feed the lightbox (single source of truth: current filtered items)
  const lightboxPhotos = items.map((it) => ({
    src: it.image,
    caption: it.title,
    location: it.location,
  }));

  const renderCard = (item) => {
    const hasStory = Boolean(item.storySlug);
    const lightboxIndex = items.findIndex((it) => it.id === item.id);
    const cardInner = (
      <>
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500" />

        {/* corner frames on hover */}
        <span className="absolute top-4 right-4 w-7 h-7 border-t border-r border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <span className="absolute bottom-4 left-4 w-7 h-7 border-b border-l border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {hasStory && (
          <span className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 border border-[#c9a96e]/80 bg-black/50 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-[#e8d5a8] animate-pulse" />
            <span className="text-[#e8d5a8] text-[9px] tracking-[0.4em] uppercase">Featured Story</span>
          </span>
        )}

        <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
          <p className="text-[#c9a96e] text-[10px] tracking-[0.4em] uppercase mb-2">
            {item.category}
          </p>
          <h3 className="font-serif italic text-white text-2xl md:text-3xl leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {item.title}
          </h3>
          <p className="text-white/60 text-xs tracking-wider mt-2">{item.location}</p>
          {hasStory && (
            <p className="mt-3 inline-flex items-center gap-2 text-[#e8d5a8] text-[10px] tracking-[0.4em] uppercase">
              <span className="block w-6 h-px bg-[#c9a96e]" />
              View Story
            </p>
          )}
        </div>
      </>
    );

    return hasStory ? (
      <Link
        key={item.id}
        to={`/portfolio/${item.storySlug}`}
        data-testid={`portfolio-story-card-${item.storySlug}`}
        className="group relative aspect-[4/3] overflow-hidden cursor-pointer bg-black block"
      >
        {cardInner}
      </Link>
    ) : (
      <div
        key={item.id}
        onClick={() => lightbox.openAt(lightboxIndex)}
        data-testid={`portfolio-photo-${item.id}`}
        className="group relative aspect-[4/3] overflow-hidden cursor-zoom-in bg-black"
      >
        {cardInner}
      </div>
    );
  };

  // Insert elegant gold "View Story" CTA in the middle of the grid
  const featured = portfolioItems.find((p) => p.storySlug);
  const half = Math.ceil(items.length / 2);
  const firstHalf = items.slice(0, half);
  const secondHalf = items.slice(half);

  return (
    <section id="portfolio" className="bg-[#0a0a0a] py-28 lg:py-40 px-6 lg:px-12">
      <div className="max-w-[1500px] mx-auto">
        <div className="text-center mb-14">
          <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-6">Our Work</p>
          <h2 className="font-serif text-white text-6xl md:text-7xl lg:text-8xl">Portfolio</h2>
          <div className="w-12 h-px bg-[#c9a96e] mx-auto mt-8" />
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-14">
          {categories.map((c) => (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              className={`px-7 py-3 text-[11px] tracking-[0.35em] uppercase border transition-colors duration-300 ${
                filter === c.key
                  ? 'bg-[#e8d5a8] border-[#e8d5a8] text-black'
                  : 'border-white/15 text-white/70 hover:border-[#c9a96e] hover:text-[#c9a96e]'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
          {firstHalf.map((item) => renderCard(item))}
        </div>

        {/* Gold "View Story" CTA - centered in the middle of the portfolio */}
        {featured && (
          <div className="relative my-16 lg:my-24 flex flex-col items-center justify-center text-center">
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="w-72 h-72 rounded-full bg-[#c9a96e]/10 blur-3xl" />
            </div>
            <div className="relative flex items-center justify-center gap-6 mb-8">
              <span className="block w-20 sm:w-28 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
              <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
              <span className="block w-20 sm:w-28 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
            </div>
            <p className="relative text-[#c9a96e] text-[11px] tracking-[0.6em] uppercase mb-5">Featured Wedding</p>
            <h3
              className="relative font-serif italic text-white text-3xl md:text-4xl lg:text-5xl max-w-2xl leading-snug mb-10"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              A fairy tale composed in willow light.
            </h3>

            <Link
              to={`/portfolio/${featured.storySlug}`}
              data-testid="portfolio-view-story-link"
              className="relative group inline-block px-14 sm:px-20 py-6 sm:py-7 border border-[#c9a96e] bg-transparent hover:bg-[#c9a96e]/10 transition-colors duration-500"
            >
              <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#e8d5a8]" />
              <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#e8d5a8]" />
              <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#e8d5a8]" />
              <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#e8d5a8]" />
              <span
                className="font-serif italic text-[#e8d5a8] text-2xl sm:text-3xl tracking-[0.2em]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                View Story
              </span>
            </Link>

            <div className="relative flex items-center justify-center gap-6 mt-8">
              <span className="block w-20 sm:w-28 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
              <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
              <span className="block w-20 sm:w-28 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
          {secondHalf.map((item) => renderCard(item))}
        </div>
      </div>

      <Lightbox
        open={lightbox.open}
        onClose={lightbox.close}
        photos={lightboxPhotos}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
      />
    </section>
  );
};

export default Portfolio;
